import { firstValueFrom, Observable, timeout, TimeoutError } from 'rxjs'
import {
  Configuration,
  DocumentsApi as DocumentsApiClientV5,
  IDocumentsApi,
} from '@taxfyle/documents-api-client/v5'
import {
  APIClient,
  createRealtimeEvents,
  resolveBaseURL,
} from 'infra/APIClient'
import { RealtimeEvent } from 'infra/RealTimeEvents'
import { CursorPaged, APIConfig } from 'types/definitions'
import {
  DocumentDTO,
  DocumentPopulate,
  FindDocumentsQuery,
  CreateDocumentPayload,
  UpdateDocumentPayload,
  ZipFilesPayload,
  AssignDocumentTypePayload,
  SearchDocumentsQuery,
} from './dtos/document'
import {
  DocumentTypeDTO,
  GetManyDocumentTypesPayload,
} from './dtos/document-type'
import { CreateDocumentAccessPayload } from './dtos/document-access'
import {
  DocumentVersionsQuery,
  CreateDocumentVersionPayload,
  DocumentVersionDTO,
  DocumentVersionQuery,
} from './dtos/document-version'
import { AxiosRequestConfig } from 'axios'
import { ConfirmDocumentPayload } from './dtos/client-data-verification'

export interface DocumentAPI {
  documents: DocumentsAPI
  access: DocumentAccessAPI
  version: DocumentVersionAPI
  types: DocumentTypeAPI
  clientDataVerification: ClientDataVerificationAPI
}

export function createDocumentAPI(opts: APIConfig): DocumentAPI {
  return {
    documents: new DocumentsAPI(opts),
    access: new DocumentAccessAPI(opts),
    version: new DocumentVersionAPI(opts),
    types: new DocumentTypeAPI(opts),
    clientDataVerification: new ClientDataVerificationAPI(opts),
  }
}

/**
 * The v5 Documents API.
 */
export interface DocumentsApiV5 {
  /**
   * The API client.
   */
  documents: IDocumentsApi

  /**
   * The base URL the client is configured with.
   */
  baseURL: string

  /**
   * Emits every realtime event on the connection.
   * Don't subscribe until there is a token.
   */
  events<T extends RealtimeEvent>(): Observable<T>
}

/**
 * Creates the v5 Documents API client.
 */
export function createDocumentsApiV5(opts: APIConfig): DocumentsApiV5 {
  const getToken = async () => {
    try {
      return await firstValueFrom(opts.token$.pipe(timeout(10_000)))
    } catch (err: unknown) {
      if (err instanceof TimeoutError) {
        throw new Error('Timed out waiting for a valid token')
      }
      throw err
    }
  }

  return {
    documents: new DocumentsApiClientV5(
      new Configuration({
        basePath: resolveBaseURL(opts.baseURL),
        // A falsy token makes the generated runtime silently omit the
        // Authorization header, so the getter must throw on failure.
        accessToken: getToken,
      }),
    ),
    baseURL: opts.baseURL,
    events: <T extends RealtimeEvent>() => createRealtimeEvents<T>(opts),
  }
}

export class DocumentsAPI extends APIClient {
  find(query: FindDocumentsQuery): Promise<CursorPaged<DocumentDTO>> {
    return this.client.get('/v3/documents', { params: query }).then(this._data)
  }

  search(query: SearchDocumentsQuery): Promise<CursorPaged<DocumentDTO>> {
    return this.client
      .get('/v3/documents/search', { params: query })
      .then(this._data)
  }

  get(id: string, params?: DocumentPopulate): Promise<DocumentDTO | null> {
    return this.client.get(`/v3/documents/${id}`, { params }).then(this._data)
  }

  create(data: CreateDocumentPayload): Promise<DocumentDTO> {
    return this.client.post('/v3/documents', data).then(this._data)
  }

  update(id: string, data: UpdateDocumentPayload) {
    return this.client
      .patch(`/v3/documents/${id}`, data, {
        params: { accesses: true, history: true },
      })
      .then(this._data)
  }

  archive(id: string): Promise<DocumentDTO> {
    return this.client.patch(`/v3/documents/${id}/archive`).then(this._data)
  }

  zipFiles(data: ZipFilesPayload): Promise<DocumentDTO> {
    return this.client.post(`/v3/documents/zipFiles`, data).then(this._data)
  }

  async getDownloadUrl(id: string, preview = false): Promise<{ url: string }> {
    const params = new URLSearchParams()
    if (preview !== undefined) {
      params.append('preview', preview.toString())
    }

    return this.client
      .get(`/v3/documents/${id}/download`, {
        params,
      })
      .then(this._data)
  }

  async assignType(id: string, data: AssignDocumentTypePayload) {
    return this.client
      .post(`/v3/documents/${id}/assign-type`, data)
      .then(this._data)
  }
}

export class DocumentTypeAPI extends APIClient {
  find(): Promise<DocumentTypeDTO[]> {
    return this.client.get('/v3/types').then(this._data)
  }

  get(id: string): Promise<DocumentTypeDTO> {
    return this.client.get(`/v3/types/${id}`).then(this._data)
  }

  getMany(
    ids: GetManyDocumentTypesPayload,
  ): Promise<Array<DocumentTypeDTO | null>> {
    return this.client.post(`/v3/types/id-query`, ids).then(this._data)
  }
}

export class DocumentAccessAPI extends APIClient {
  create(data: CreateDocumentAccessPayload) {
    return this.client.post('/v3/accesses', data).then(this._data)
  }

  delete(id: string) {
    return this.client.delete(`/v3/accesses/${id}`).then(this._data)
  }
}

export class DocumentVersionAPI extends APIClient {
  get({
    document_id: documentLongId,
    version_id: versionId,
  }: DocumentVersionQuery) {
    return this.client
      .get(`/v3/documents/${documentLongId}/versions/${versionId}`)
      .then(this._data)
  }

  find({ document_id: documentLongId, ...query }: DocumentVersionsQuery) {
    return this.client
      .get(`/v3/documents/${documentLongId}/versions`, { params: query })
      .then(this._data)
  }

  create(
    { document_long_id: documentLongId, ...data }: CreateDocumentVersionPayload,
    opts: CreateOpts,
  ): Promise<DocumentVersionDTO> {
    const reqOpts: AxiosRequestConfig = {}
    let body: any = data
    if (data.action.type === 'ReplaceContents') {
      body = new FormData()
      const { files, ...action } = data.action
      body.append('meta', JSON.stringify({ action, message: data.message }))
      files.forEach((f, i) =>
        body.append(`file${i + 1}`, f, sanitizeFileName(f.name)),
      )
      reqOpts.onUploadProgress = (e) => {
        opts.onProgress({
          loaded: e.loaded,
          total: e.total ?? e.loaded,
        })
      }
    }

    return this.client
      .post(`/v3/documents/${documentLongId}/versions`, body, reqOpts)
      .then(this._data)
  }
}

export class ClientDataVerificationAPI extends APIClient {
  confirm(payload: ConfirmDocumentPayload) {
    return this.client
      .post(
        `/v3/documents/${payload.document_long_id}/versions/${payload.version_id}/files/${payload.file_id}/confirm`,
        {
          job_id: payload.job_id,
        },
      )
      .then(this._data)
  }
}

/**
 * Sanitizes a file name.
 * @param {string} str
 */
function sanitizeFileName(str: string) {
  return str.replace(/\//g, '_').replace(/\\/g, '_')
}

export interface CreateOpts {
  onProgress(e: UploadProgress): void
}

export interface UploadProgress {
  total: number
  loaded: number
}
